import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

export default function IncomeTaxTable(props) {
  const { data, isLoading, totalColumnLabel = 'Total TDS' } = props;
  const scrollableBodyRef = useRef(null);
  const fixedBodyRef = useRef(null);

  // Column widths
  const columnWidths = {
    srNo: '80px',
    employeeName: '200px',
    april: '120px',
    may: '120px',
    june: '120px',
    july: '120px',
    august: '120px',
    september: '120px',
    october: '120px',
    november: '120px',
    december: '120px',
    january: '120px',
    february: '120px',
    march: '120px',
    netGross: '140px',
  };

  // Calculate widths for fixed and scrollable sections (3 fixed columns)
  const fixedColumnKeys = ['srNo', 'employeeName'];
  const fixedColumnWidths = fixedColumnKeys.map(key => columnWidths[key]);
  const fixedColumnsTotalWidth = fixedColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);
  
  const scrollableColumnKeys = Object.keys(columnWidths).filter(key => !fixedColumnKeys.includes(key));
  const scrollableColumnWidths = scrollableColumnKeys.map(key => columnWidths[key]);
  const scrollableColumnsTotalWidth = scrollableColumnWidths.reduce((sum, width) => sum + parseInt(width), 0);

  // Column names
  const fixedColumnNames = ['Sr. No.', 'Employee Name'];
  const scrollableColumnNames = [
    'April', 'May', 'June', 'July', 'August', 'September', 
    'October', 'November', 'December', 'January', 'February', 'March',
    totalColumnLabel
  ];

  // Show all records, including those without income tax data
  const filteredData = data || [];

  // Calculate totals
  const calculateTotals = () => {
    if (!filteredData || filteredData.length === 0) return {};

    const totals = {
      april: 0,
      may: 0,
      june: 0,
      july: 0,
      august: 0,
      september: 0,
      october: 0,
      november: 0,
      december: 0,
      january: 0,
      february: 0,
      march: 0,
      netGross: 0,
    };

    filteredData.forEach((item) => {
      totals.april += Number(item.April) || 0;
      totals.may += Number(item.May) || 0;
      totals.june += Number(item.June) || 0;
      totals.july += Number(item.July) || 0;
      totals.august += Number(item.August) || 0;
      totals.september += Number(item.September) || 0;
      totals.october += Number(item.October) || 0;
      totals.november += Number(item.November) || 0;
      totals.december += Number(item.December) || 0;
      totals.january += Number(item.January) || 0;
      totals.february += Number(item.February) || 0;
      totals.march += Number(item.March) || 0;
    });

    // Calculate Total TDS as sum of all monthly column totals
    totals.netGross = totals.april + totals.may + totals.june + totals.july + 
                      totals.august + totals.september + totals.october + 
                      totals.november + totals.december + totals.january + 
                      totals.february + totals.march;

    return totals;
  };

  const totals = calculateTotals();

  // Sync scroll for header and body
  useEffect(() => {
    const scrollableHeader = document.getElementById('scrollable-header-container');
    const scrollableBody = scrollableBodyRef.current;
    const fixedBody = fixedBodyRef.current;

    const syncScroll = () => {
      if (scrollableBody && scrollableHeader) {
        // Sync horizontal scroll for header
        scrollableHeader.scrollLeft = scrollableBody.scrollLeft;
      }
      if (scrollableBody && fixedBody) {
        // Move fixed columns up/down based on scroll position
        fixedBody.style.transform = `translateY(-${scrollableBody.scrollTop}px)`;
      }
    };

    scrollableBody?.addEventListener('scroll', syncScroll);

    return () => {
      scrollableBody?.removeEventListener('scroll', syncScroll);
    };
  }, [isLoading]);

  const formatAmount = (value) => {
    if (value === null || value === undefined) return '-';
    const numValue = Number(value);
    const roundedUp = Math.ceil(numValue);
    return roundedUp.toLocaleString('en-IN');
  };

  // Calculate individual row totals
  const calculateRowTotal = (item) => {
    return (Number(item.April) || 0) + 
           (Number(item.May) || 0) + 
           (Number(item.June) || 0) + 
           (Number(item.July) || 0) + 
           (Number(item.August) || 0) + 
           (Number(item.September) || 0) + 
           (Number(item.October) || 0) + 
           (Number(item.November) || 0) + 
           (Number(item.December) || 0) + 
           (Number(item.January) || 0) + 
           (Number(item.February) || 0) + 
           (Number(item.March) || 0);
  };

  return (
    <div>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : filteredData && filteredData.length > 0 ? (
        <div style={{ 
          position: 'relative', 
          height: '500px', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          
          {/* Header Container */}
          <Box 
            sx={{ 
              display: 'flex',
              position: 'sticky', 
              top: 0, 
              zIndex: 10,
              background: '#fff',
              flexShrink: 0,
              borderBottom: '2px solid #dee2e6',
              overflow: 'hidden'
            }}
          >
            {/* Fixed Header Columns */}
            <Box
              sx={{
                width: `${fixedColumnsTotalWidth}px`,
                flexShrink: 0,
                background: '#212529',
                color: 'white'
              }}
            >
              <table style={{ 
                width: '100%', 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table table-dark">
                <thead>
                  <tr>
                    {fixedColumnNames.map((name, index) => (
                      <th 
                        key={index}
                        style={{ 
                          width: fixedColumnWidths[index], 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: index === 1 ? 'left' : 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </Box>

            {/* Scrollable Header Columns */}
            <Box
              id="scrollable-header-container"
              sx={{ 
                flex: 1,
                overflowX: 'hidden',
                overflowY: 'hidden',
                background: '#212529',
                color: 'white'
              }}
            >
              <table style={{ 
                width: `${scrollableColumnsTotalWidth}px`, 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table table-dark">
                <thead>
                  <tr>
                    {scrollableColumnNames.map((name, index) => (
                      <th 
                        key={index}
                        style={{ 
                          width: scrollableColumnWidths[index], 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: 'right',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </Box>
          </Box>

          {/* Body Container - Modified for proper scrolling */}
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Fixed Body Columns - Wrapper that stays in place */}
            <Box
              sx={{ 
                width: `${fixedColumnsTotalWidth}px`,
                flexShrink: 0,
                background: '#fff',
                borderRight: '2px solid #dee2e6',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Inner container that moves with scroll */}
              <Box
                ref={fixedBodyRef}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  overflow: 'hidden'
                }}
              >
                <table style={{ 
                  width: '100%', 
                  tableLayout: 'fixed',
                  borderCollapse: 'collapse',
                  margin: 0
                }} className="table">
                  <tbody>
                    {filteredData.map((item, i) => (
                      <tr key={i} style={{ height: '42px' }}>
                        <td style={{ 
                          width: columnWidths.srNo, 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          {i + 1}
                        </td>
                        <td style={{ 
                          width: columnWidths.employeeName, 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: 'left',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          {item.displayName || (item.first_name && item.last_name 
                            ? `${item.first_name} ${item.last_name}` 
                            : item.employee_id ? `Employee ID: ${item.employee_id}` : 'N/A')}
                        </td>
                      </tr>
                    ))}

                    {/* Totals Row in Fixed Columns */}
                    {filteredData.length > 0 && (
                      <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', height: '42px' }}>
                        <td style={{ 
                          width: columnWidths.srNo, 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>Total</td>
                        <td style={{ 
                          width: columnWidths.employeeName, 
                          padding: '8px', 
                          boxSizing: 'border-box', 
                          textAlign: 'center',
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6'
                        }}>Total</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </Box>

            {/* Scrollable Body Columns - Has the main scroll */}
            <Box
              ref={scrollableBodyRef}
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                background: '#fff'
              }}
            >
              <table style={{ 
                width: `${scrollableColumnsTotalWidth}px`, 
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0
              }} className="table">
                <tbody>
                  {filteredData.map((item, i) => (
                    <tr key={i} style={{ height: '42px' }}>
                      <td style={{ 
                        width: columnWidths.april, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.April)}</td>
                      <td style={{ 
                        width: columnWidths.may, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.May)}</td>
                      <td style={{ 
                        width: columnWidths.june, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.June)}</td>
                      <td style={{ 
                        width: columnWidths.july, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.July)}</td>
                      <td style={{ 
                        width: columnWidths.august, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.August)}</td>
                      <td style={{ 
                        width: columnWidths.september, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.September)}</td>
                      <td style={{ 
                        width: columnWidths.october, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.October)}</td>
                      <td style={{ 
                        width: columnWidths.november, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.November)}</td>
                      <td style={{ 
                        width: columnWidths.december, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.December)}</td>
                      <td style={{ 
                        width: columnWidths.january, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.January)}</td>
                      <td style={{ 
                        width: columnWidths.february, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.February)}</td>
                      <td style={{ 
                        width: columnWidths.march, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(item.March)}</td>
                      <td style={{ 
                        width: columnWidths.netGross, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #dee2e6'
                      }}>
                        {formatAmount(calculateRowTotal(item))}
                      </td>
                    </tr>
                  ))}

                  {/* Totals Row in Scrollable Columns */}
                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', height: '42px' }}>
                      <td style={{ 
                        width: columnWidths.april, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.april)}</td>
                      <td style={{ 
                        width: columnWidths.may, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.may)}</td>
                      <td style={{ 
                        width: columnWidths.june, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.june)}</td>
                      <td style={{ 
                        width: columnWidths.july, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.july)}</td>
                      <td style={{ 
                        width: columnWidths.august, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.august)}</td>
                      <td style={{ 
                        width: columnWidths.september, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.september)}</td>
                      <td style={{ 
                        width: columnWidths.october, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.october)}</td>
                      <td style={{ 
                        width: columnWidths.november, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.november)}</td>
                      <td style={{ 
                        width: columnWidths.december, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.december)}</td>
                      <td style={{ 
                        width: columnWidths.january, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.january)}</td>
                      <td style={{ 
                        width: columnWidths.february, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.february)}</td>
                      <td style={{ 
                        width: columnWidths.march, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.march)}</td>
                      <td style={{ 
                        width: columnWidths.netGross, 
                        padding: '8px', 
                        boxSizing: 'border-box', 
                        textAlign: 'right',
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid #dee2e6'
                      }}>{formatAmount(totals.netGross)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </div>
      ) : (
        <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
          No data available.
        </Typography>
      )}
    </div>
  );
}

IncomeTaxTable.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalColumnLabel: PropTypes.string,
};